import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export interface ExportColumn<T> {
  header: string
  accessor: (row: T) => string | number | undefined | null
}

export interface ReportBrandingOptions {
  companyName: string
  tradeName?: string | null
  cnpj?: string | null
  logoUrl?: string | null
  primaryColor?: string
  reportTitle: string
  periodLabel: string
  generatedAt?: Date
  confidential?: boolean
}

export function hexToRgb(hex?: string | null): [number, number, number] {
  const DEFAULT_RGB: [number, number, number] = [143, 107, 72] // #8F6B48 padrao Seven
  if (!hex || typeof hex !== 'string') return DEFAULT_RGB

  let sanitized = hex.replace(/^#/, '').trim()
  if (sanitized.length === 3) {
    sanitized = sanitized.split('').map(char => char + char).join('')
  }
  if (sanitized.length !== 6) return DEFAULT_RGB

  const num = parseInt(sanitized, 16)
  if (isNaN(num)) return DEFAULT_RGB

  const r = (num >> 16) & 255
  const g = (num >> 8) & 255
  const b = num & 255

  return [r, g, b]
}

export function sanitizeForSpreadsheet(val: unknown): string {
  if (val === null || val === undefined) return ''
  const str = String(val)
  if (/^[=+\-@\t\r]/.test(str)) {
    return `'${str}`
  }
  return str
}

export function exportToCSV<T>(data: T[], columns: ExportColumn<T>[], filename: string) {
  if (!data || !data.length) return

  // Create CSV header row
  const headers = columns.map(col => `"${col.header.replace(/"/g, '""')}"`).join(',')
  
  // Create CSV data rows with cell sanitization against formula injection
  const rows = data.map(row => {
    return columns.map(col => {
      const rawVal = col.accessor(row)
      const sanitized = sanitizeForSpreadsheet(rawVal)
      return `"${sanitized.replace(/"/g, '""')}"`
    }).join(',')
  })

  const csvContent = [headers, ...rows].join('\n')
  
  // Add BOM for Excel UTF-8 compatibility
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export async function copyToClipboard<T>(data: T[], columns: ExportColumn<T>[]) {
  if (!data || !data.length) return false

  const headers = columns.map(col => col.header).join('\t')
  const rows = data.map(row => {
    return columns.map(col => {
      let val = col.accessor(row)
      if (val === null || val === undefined) val = ''
      return String(val).replace(/\t/g, ' ').replace(/\n/g, ' ')
    }).join('\t')
  })

  const tsvContent = [headers, ...rows].join('\n')
  
  try {
    await navigator.clipboard.writeText(tsvContent)
    return true
  } catch (err) {
    console.error('Falha ao copiar', err)
    throw err
  }
}

export function exportArraysToXLSX(
  headers: string[],
  rows: (string | number | undefined | null)[][],
  filename: string,
  brandingOptions?: ReportBrandingOptions
) {
  if (!headers.length || !rows.length) return

  const sanitizedRows = rows.map(row =>
    row.map(val => (typeof val === 'string' ? sanitizeForSpreadsheet(val) : val ?? ''))
  )

  const aoa: (string | number)[][] = []

  if (brandingOptions) {
    const titleLine = `${brandingOptions.tradeName || brandingOptions.companyName} - ${brandingOptions.reportTitle}`
    const cnpjLine = `${brandingOptions.cnpj ? `CNPJ: ${brandingOptions.cnpj} | ` : ''}Período: ${brandingOptions.periodLabel}`
    const dateLine = `Gerado em: ${(brandingOptions.generatedAt || new Date()).toLocaleString('pt-BR')}${brandingOptions.confidential ? ' | Confidencial' : ''}`
    
    aoa.push([titleLine])
    aoa.push([cnpjLine])
    aoa.push([dateLine])
    aoa.push([])
  }

  aoa.push(headers)
  aoa.push(...sanitizedRows)

  const worksheet = XLSX.utils.aoa_to_sheet(aoa)

  // Auto-fit column widths based on table headers and row contents
  const colWidths = headers.map((header, idx) => {
    let maxLen = String(header).length
    for (const row of sanitizedRows) {
      const cellVal = row[idx]
      if (cellVal !== undefined && cellVal !== null) {
        maxLen = Math.max(maxLen, String(cellVal).length)
      }
    }
    return { wch: Math.max(maxLen + 2, 10) }
  })
  worksheet['!cols'] = colWidths

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Export')

  XLSX.writeFile(workbook, `${filename}.xlsx`)
}

export function exportToXLSX<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string,
  brandingOptions?: ReportBrandingOptions
) {
  if (!data || !data.length) return

  const headers = columns.map(col => col.header)
  const rows = data.map(row => {
    return columns.map(col => {
      const val = col.accessor(row)
      return val === null || val === undefined ? '' : val
    })
  })

  exportArraysToXLSX(headers, rows, filename, brandingOptions)
}

export function exportArraysToPDF(
  headers: string[],
  rows: (string | number | undefined | null)[][],
  filename: string,
  options?: ReportBrandingOptions
) {
  if (!headers.length || !rows.length) return

  const doc = new jsPDF({ orientation: 'landscape' })
  const primaryRgb = hexToRgb(options?.primaryColor)

  const stringRows = rows.map(row =>
    row.map(val => (val === null || val === undefined ? '' : String(val)))
  )

  // @ts-ignore - jspdf-autotable adds autoTable to jsPDF instance
  autoTable(doc, {
    head: [headers],
    body: stringRows,
    theme: 'grid',
    styles: { fontSize: 8 },
    headStyles: { fillColor: primaryRgb }
  })

  doc.save(`${filename}.pdf`)
}

export function exportToPDF<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string,
  options?: ReportBrandingOptions
) {
  if (!data || !data.length) return

  const headers = columns.map(col => col.header)
  const rows = data.map(row => {
    return columns.map(col => {
      const val = col.accessor(row)
      return val === null || val === undefined ? '' : val
    })
  })

  exportArraysToPDF(headers, rows, filename, options)
}

export function exportArraysToCSV(headers: string[], rows: string[][], filename: string) {
  if (!headers.length || !rows.length) return

  const csvHeaders = headers.map(h => `"${h.replace(/"/g, '""')}"`).join(',')
  const csvRows = rows.map(row => row.map(val => `"${sanitizeForSpreadsheet(val).replace(/"/g, '""')}"`).join(','))

  const csvContent = [csvHeaders, ...csvRows].join('\n')
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export async function copyArraysToClipboard(headers: string[], rows: string[][]) {
  if (!headers.length || !rows.length) return false

  const tsvHeaders = headers.join('\t')
  const tsvRows = rows.map(row => row.map(val => String(val).replace(/\t/g, ' ').replace(/\n/g, ' ')).join('\t'))

  const tsvContent = [tsvHeaders, ...tsvRows].join('\n')
  
  try {
    await navigator.clipboard.writeText(tsvContent)
    return true
  } catch (err) {
    console.error('Falha ao copiar', err)
    throw err
  }
}
